// Reading a price out of what someone typed into the search box.
//
// This lived inside HomepageSearchHeader as a private function, so nothing
// could test it, and it had two faults that only a test would show.
//
// The first was French. "moins de 500k" means under $500,000. The minimum
// patterns included a bare "de" and ran before the maximum patterns, so "de
// 500k" matched inside "moins de 500k" and the search returned everything
// ABOVE half a million - the opposite of the request. Maximums are now tried
// first, and the phrases that contain "de" are matched as whole phrases.
//
// The second was vocabulary: от, до, под, дешевле, максимум, более, млн, тыс.
// This site is English and French. Those words came in with the template and
// could never match anything a visitor here would type.
//
// A bare number is deliberately cautious. "2 beds 2020" must not read 2020 as a
// price, and "Toronto 3" must not become $3,000,000, so a number is only a
// price when a word or a suffix says it is, or when it is large enough that it
// cannot be anything else.

import { PROVINCES, findProvince, foldPlace } from '@/lib/provinces';
import { takeArea, takeFeatures, type FoundFeatures } from '@/lib/search-features';

export interface ParsedPrice {
  minPrice?: number;
  maxPrice?: number;
  /** What is left of the text once the price words are taken out. */
  cleaned: string;
}

/** Below this, a bare number is more likely a bedroom count or a year than a price. */
const BARE_NUMBER_IS_A_PRICE = 5000;
const BARE_NUMBER_IS_A_RENT = 300;

const MILLION_WORDS = ['million', 'millions', 'm'];
const THOUSAND_WORDS = ['thousand', 'k'];
const SUFFIX = `(${[...MILLION_WORDS, ...THOUSAND_WORDS].join('|')})`;
// "600 000" and "600,000" are one number: groups of three after a space or a
// comma. "2,5" is still two and a half.
const NUMBER = String.raw`(\d{1,3}(?:[ \u00a0\u202f,]\d{3})+(?![.,]?\d)|\d+(?:[.,]\d+)?)`;

// Longest first, so "moins de" is tried before "de" can match inside it.
const MAX_WORDS = [
  "jusqu'à", 'jusqua', 'moins de', 'au plus', 'maximum', 'less than', 'up to',
  'under', 'below', 'max', 'sous',
];
const MIN_WORDS = [
  'à partir de', 'a partir de', 'starting at', 'at least', 'plus de',
  'above', 'over', 'from', 'min', 'dès', 'des',
];

const alt = (words: string[]) => words.map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|');

/** Turns "1.5" + "m" into 1500000, and decides what a bare number means. */
export function parseValueAndSuffix(valStr: string, suffixStr: string, rent = false): number {
  const raw = String(valStr);
  const grouped = /^\d{1,3}(?:[ \u00a0\u202f,]\d{3})+$/.test(raw);
  const num = parseFloat(grouped ? raw.replace(/[ \u00a0\u202f,]/g, '') : raw.replace(',', '.'));
  if (!Number.isFinite(num)) return NaN;
  const suf = (suffixStr || '').toLowerCase().trim();
  if (MILLION_WORDS.includes(suf)) return num * 1_000_000;
  if (THOUSAND_WORDS.includes(suf)) return num * 1_000;
  // A rent is written as it is: "1500" is $1,500 a month (rentals, 2026-09-24).
  if (rent) return num;
  // No suffix: "1.5" means a million and a half, "500" means five hundred
  // thousand. Both are how people write prices out loud.
  if (num < 100) return num * 1_000_000;
  if (num <= 999) return num * 1_000;
  return num;
}

function swapIfInverted(a: number, b: number): [number, number] {
  return a > b ? [b, a] : [a, b];
}

export function parsePriceQuery(text: string, rent = false): ParsedPrice {
  const cleaned = (text || '').trim();
  if (!cleaned) return { cleaned: '' };

  // 1. A range: "300 to 500k", "de 300 à 500 k", "400k - 600k".
  const range = new RegExp(
    `(?:from|de|entre|between)?\\s*${NUMBER}\\s*${SUFFIX}?\\s*(?:to|à|a|-|et|and)\\s*${NUMBER}\\s*${SUFFIX}?`,
    'i'
  );
  const rangeMatch = cleaned.match(range);
  if (rangeMatch) {
    const [, v1, s1, v2, s2] = rangeMatch;
    // A range written "300 to 500k" means 300k to 500k: the suffix at the end
    // carries back to the beginning.
    const low = parseValueAndSuffix(v1, s1 || s2 || '', rent);
    const high = parseValueAndSuffix(v2, s2 || s1 || '', rent);
    if (Number.isFinite(low) && Number.isFinite(high)) {
      const [minPrice, maxPrice] = swapIfInverted(low, high);
      return { minPrice, maxPrice, cleaned: cleaned.replace(rangeMatch[0], ' ').trim() };
    }
  }

  // 2. A maximum, BEFORE the minimum, because "moins de" contains "de".
  const max = new RegExp(`(?:${alt(MAX_WORDS)})\\s*\\$?\\s*${NUMBER}\\s*${SUFFIX}?`, 'i');
  const maxMatch = cleaned.match(max);
  if (maxMatch) {
    const value = parseValueAndSuffix(maxMatch[1], maxMatch[2] || '', rent);
    if (Number.isFinite(value)) {
      return { maxPrice: value, cleaned: cleaned.replace(maxMatch[0], ' ').trim() };
    }
  }

  // 3. A minimum.
  const min = new RegExp(`(?:${alt(MIN_WORDS)})\\s*\\$?\\s*${NUMBER}\\s*${SUFFIX}?`, 'i');
  const minMatch = cleaned.match(min);
  if (minMatch) {
    const value = parseValueAndSuffix(minMatch[1], minMatch[2] || '', rent);
    if (Number.isFinite(value)) {
      return { minPrice: value, cleaned: cleaned.replace(minMatch[0], ' ').trim() };
    }
  }

  // 4. A number on its own, taken as a ceiling - but only when it says it is a
  // price. A suffix says so, a dollar sign says so, and so does being too large
  // to be a year or a room count.
  const bare = new RegExp(`(\\$)?\\s*${NUMBER}\\s*${SUFFIX}?`, 'i');
  const bareMatch = cleaned.match(bare);
  if (bareMatch) {
    const [, dollar, value, suffix] = bareMatch;
    const raw = parseFloat(String(value).replace(',', '.'));
    // A monthly rent is smaller: from $300 a number on its own is one.
    if (dollar || suffix || raw >= (rent ? BARE_NUMBER_IS_A_RENT : BARE_NUMBER_IS_A_PRICE)) {
      const parsed = parseValueAndSuffix(value, suffix || '', rent);
      if (Number.isFinite(parsed)) {
        return { maxPrice: parsed, cleaned: cleaned.replace(bareMatch[0], ' ').trim() };
      }
    }
  }

  return { cleaned };
}

export interface ParsedQuery {
  city?: string;
  /** Two-letter code, when the phrase names a province or territory. */
  province?: string;
  bedrooms?: number;
  bathrooms?: number;
  minPrice?: number;
  maxPrice?: number;
  propertyType?: 'Condo' | 'Single-Family Home' | 'Townhouse' | 'Multi-Family';
  petFriendly?: FoundFeatures['petFriendly'];
  furnished?: FoundFeatures['furnished'];
  garageSpaces?: number;
  amenities?: string[];
  /** Square feet. */
  minArea?: number;
  tag?: FoundFeatures['tag'];
  /** Said in the phrase: "à louer", "for rent" - or "for sale", "à vendre". */
  deal?: 'sale' | 'rent';
}

// The kind of deal a phrase names (rentals, 2026-09-24). Taken out of the
// phrase so it does not become part of a place name ("Rent Montreal").
// Bounded by spaces, not \b: \b does not see a word start before "à".
const RENT_WORDS = /(?:^|\s)(for rent|to rent|à louer|a louer|rentals?|locations?|louer|rent)(?=\s|$)/i;
const SALE_WORDS = /(?:^|\s)(for sale|à vendre|a vendre|to buy|acheter|buy)(?=\s|$)/i;

const ROOM_RANGE = String.raw`(\d+)\s*(?:-|–|to|à|a)\s*(\d+)`;

// Bedrooms, "at least" as the filter reads them; a range ("2-3 beds",
// "2 à 3 chambres") keeps its lower end. "ch" and "cac" (chambre à coucher)
// are French shorthand and come after the longer words they would cut short.
const BED_WORDS = String.raw`(?:bedrooms?|beds?|bdrms?|br|chambres?|cac|ch)\b`;
const BEDROOM_RANGE = new RegExp(String.raw`${ROOM_RANGE}\s*${BED_WORDS}`, 'i');
const BEDROOMS = new RegExp(String.raw`(\d+)\s*${BED_WORDS}`, 'i');

// Bathrooms, the same way. Without this "2 baths" stayed in the phrase and
// became part of the place name ("2 Baths Toronto").
const BATH_WORDS = String.raw`(?:bathrooms?|baths?|ba|salles? de bains?|salles? d'eau|sdb)\b`;
const BATHROOM_RANGE = new RegExp(String.raw`${ROOM_RANGE}\s*${BATH_WORDS}`, 'i');
const BATHROOMS = new RegExp(String.raw`(\d+)(?:[.,]5)?\s*${BATH_WORDS}`, 'i');

// Quebec counts a home's rooms, kitchen and living room included, with the
// bathroom as the half: a "4½" has two bedrooms, a "5½" three (owner's
// request, 2026-09-24). Written 4½, 4 ½, 4 1/2 or "4 et demie"; "pièces" may
// follow. A 1½ or 2½ is a studio - no bedroom to filter on.
const QUEBEC_ROOMS = /(?<![\d.,])(\d{1,2})\s*(?:½|1\/2|et demie?)(?:\s*(?:pièces?|pieces?|pcs?))?(?![\d/])/i;
export const bedroomsFromQuebecRooms = (rooms: number): number | undefined => (rooms >= 3 && rooms <= 12 ? rooms - 2 : undefined);

// Property type. Each entry is tried in order and the first that matches wins,
// so "townhouse" is checked before "house" could match inside it.
const TYPES: Array<{ type: NonNullable<ParsedQuery['propertyType']>; words: RegExp }> = [
  { type: 'Townhouse', words: /(?<![\p{L}\d])(townhouses?|town homes?|maisons? de ville|maisons? en rangée)(?![\p{L}\d])/giu },
  { type: 'Multi-Family', words: /\b(multi-?family|duplex|triplex|plex)\b/gi },
  { type: 'Condo', words: /\b(condos?|appartements?|apartments?|lofts?|copropriétés?)\b/gi },
  { type: 'Single-Family Home', words: /\b(houses?|maisons?|cottages?|bungalows?|unifamiliales?|chalets?)\b/gi },
];

// Words that say nothing about the place: "homes for sale in", "annonces à".
const NOISE = /\b(for sale|à vendre|a vendre|real estate|immobilier|studios?|homes?|properties|property|propriétés?|listings?|annonces?)\b/gi;

// Filler a person puts before or after a place name. Taken off both ends,
// repeatedly - "in", then "à" - once everything else is gone; before, it was
// tried while the phrase still began with a space and never matched, so
// "House in Laval" searched for a city called "In Laval".
const FILLER = '(?:in|at|on|near|for|sale|a|à|au|aux|dans|de|du|des|en|près de|pres de)';
const LEADING_FILLER = new RegExp(String.raw`^${FILLER}\s+`, 'i');
const TRAILING_FILLER = new RegExp(String.raw`\s+${FILLER}$`, 'i');

// "Quebec City" and "Ville de Québec" are the city, not the province.
const QUEBEC_CITY = /^(quebec city|ville de quebec|quebec \(ville\)|quebec ville)$/;

/** Takes a province named at the end ("Laval QC", "Montréal, Québec") or on its own. */
function splitProvince(text: string): { place: string; province?: string } {
  const folded = foldPlace(text);
  if (!folded) return { place: '' };
  if (QUEBEC_CITY.test(folded)) return { place: 'Québec', province: 'QC' };
  const whole = findProvince(text);
  if (whole) return { place: '', province: whole.code };

  const names = PROVINCES.flatMap((p) => [
    { code: p.code, name: foldPlace(p.en) },
    { code: p.code, name: foldPlace(p.fr) },
  ]).sort((a, b) => b.name.length - a.name.length);
  for (const { code, name } of names) {
    if (folded.endsWith(` ${name}`)) {
      const words = name.split(' ').length;
      const kept = text.trim().split(/\s+/).slice(0, -words).join(' ');
      return { place: kept.replace(/[,\s]+$/, ''), province: code };
    }
  }
  // A two-letter code at the end only when written as one - capitals, or after
  // a comma: "on" and "ns" are words too.
  const code = text.trim().match(/(?:,\s*|\s+)([A-Za-z]{2})$/);
  if (code && (code[1] === code[1].toUpperCase() || /,\s*[A-Za-z]{2}$/.test(text.trim()))) {
    const p = PROVINCES.find((x) => x.code === code[1].toUpperCase());
    if (p) return { place: text.trim().slice(0, -code[0].length).replace(/[,\s]+$/, ''), province: p.code };
  }
  return { place: text };
}

const tidy = (text: string) => text.replace(/\s+/g, ' ').replace(/^[.,\s-]+|[.,\s-]+$/g, '').trim();

function stripFiller(text: string): string {
  let rest = tidy(text);
  for (let i = 0; i < 4; i++) {
    const next = tidy(rest.replace(LEADING_FILLER, '').replace(TRAILING_FILLER, ''));
    if (next === rest) break;
    rest = next;
  }
  return rest;
}

/**
 * Reads a whole search phrase: bedrooms, bathrooms, price, property type, a
 * province, and whatever is left, which is treated as a place name.
 *
 * The order matters. Rooms come out first so "2 beds" cannot be read as a
 * price, and the price comes out before the type so "500k condo" leaves only
 * the word condo behind.
 */
export function parseSearchQuery(text: string, options: { deal?: 'sale' | 'rent' } = {}): ParsedQuery {
  // A phrase is a few words; a link can carry thousands, and the number patterns
  // below are quadratic on a long run of digits.
  let rest = tidy((text || '').slice(0, MAX_PHRASE));
  if (!rest) return {};
  const parsed: ParsedQuery = {};

  const rentWord = rest.match(RENT_WORDS);
  const saleWord = rest.match(SALE_WORDS);
  if (rentWord) {
    parsed.deal = 'rent';
    rest = rest.replace(new RegExp(RENT_WORDS.source, 'gi'), ' ');
  } else if (saleWord) {
    parsed.deal = 'sale';
    rest = rest.replace(new RegExp(SALE_WORDS.source, 'gi'), ' ');
  }
  const rent = (parsed.deal ?? options.deal) === 'rent';

  const room = (range: RegExp, single: RegExp): number | undefined => {
    const m = rest.match(range) || rest.match(single);
    if (!m) return undefined;
    rest = rest.replace(m[0], ' ');
    const count = parseInt(m[1], 10);
    return Number.isFinite(count) && count > 0 && count <= 20 ? count : undefined;
  };
  const beds = room(BEDROOM_RANGE, BEDROOMS);
  if (beds) parsed.bedrooms = beds;
  const quebec = rest.match(QUEBEC_ROOMS);
  if (quebec) {
    rest = rest.replace(quebec[0], ' ');
    const fromRooms = bedroomsFromQuebecRooms(parseInt(quebec[1], 10));
    // Bedrooms named outright win over the count of rooms.
    if (fromRooms && !parsed.bedrooms) parsed.bedrooms = fromRooms;
  }
  const baths = room(BATHROOM_RANGE, BATHROOMS);
  if (baths) parsed.bathrooms = baths;

  const area = takeArea(rest);
  rest = area.rest;
  if (area.minArea) parsed.minArea = area.minArea;

  const price = parsePriceQuery(rest, rent);
  if (price.minPrice) parsed.minPrice = price.minPrice;
  if (price.maxPrice) parsed.maxPrice = price.maxPrice;
  rest = price.cleaned;

  const features = takeFeatures(rest);
  rest = features.rest;
  Object.assign(parsed, features.found);
  // Furnished means something only for a rental; on a sale it would empty the results.
  if (!rent) delete parsed.furnished;
  // "with pool and garage": once the features are gone, the joining words stay.
  if (Object.keys(features.found).length) rest = rest.replace(/(?<![\p{L}\d-])(?:with|avec|and|et|w\/|&)(?![\p{L}\d-])/giu, ' ');

  for (const { type, words } of TYPES) {
    words.lastIndex = 0;
    if (words.test(rest)) {
      parsed.propertyType = type;
      rest = rest.replace(words, ' ');
      break;
    }
  }


  rest = stripFiller(rest.replace(NOISE, ' '));
  const { place, province } = splitProvince(rest);
  if (province) parsed.province = province;
  // A number left on its own ("2 beds 2020") is no place name; a street
  // number with its street ("123 Rue Sherbrooke") stays, for the text search.
  const city = /^\d+$/.test(stripFiller(place)) ? '' : stripFiller(place);

  if (city.length >= 2) {
    // Title case, but leave an already-capitalised name alone so Montréal and
    // Trois-Rivières survive.
    parsed.city = city
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  return parsed;
}

/** The longest phrase read; the search box allows no more. */
export const MAX_PHRASE = 200;

/** True when the phrase said something besides a place: rooms, price, type, features, area, tag. */
export function hasNonPlaceParts(n: ParsedQuery): boolean {
  return Boolean(
    n.province || n.bedrooms || n.bathrooms || n.minPrice || n.maxPrice || n.propertyType ||
      n.petFriendly || n.furnished || n.garageSpaces || n.amenities?.length || n.minArea || n.tag
  );
}

/** The results-page address parts of the features of a phrase. */
export function featureParams(n: ParsedQuery): Record<string, string> {
  const out: Record<string, string> = {};
  if (n.petFriendly) out.petFriendly = 'yes';
  if (n.furnished) out.furnished = n.furnished;
  if (n.garageSpaces) out.garageSpaces = String(n.garageSpaces);
  if (n.amenities?.length) out.amenities = n.amenities.join(',');
  if (n.minArea) out.minArea = String(n.minArea);
  if (n.tag) out.tag = n.tag;
  return out;
}
